"use client";
import React, { useMemo, useState } from "react";
import { BadgePercent, Edit3Icon, Plus, Trash2, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useStates } from "../context/StatesContext";

export default function States() {
const { states, addState, updateState, deleteState, updateStateStatus } = useStates();


  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [status, setStatus] = useState("active");

  /* ---------- FILTER & SORT ---------- */
  const filtered = useMemo(() => {
    let data = [...states].filter((s) =>
      s.name.toLowerCase().includes(search.toLowerCase())
    );
    if (sortBy === "az") data.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "za") data.sort((a, b) => b.name.localeCompare(a.name));
    return data;
  }, [states, search, sortBy]);

  /* ---------- MODAL HELPERS ---------- */
  const openModal = (item = null) => {
    setEditing(item);
    setName(item?.name ?? "");
    setStatus(item?.status ?? "active");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setName("");
    setStatus("active");
  };

  /* ---------- ACTIONS ---------- */
  const save = async () => {
    if (!name.trim()) return;
    try {
      if (editing) {
        await updateState(editing.id, { name, status });
      } else {
       await addState({ name, status }); 
      }
      closeModal();
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
    }
  };

  const toggleStatusHandler = (id, currentStatus) => {
    updateStateStatus(id, currentStatus === "active" ? "inactive" : "active");
  };

  const removeHandler = (id) => {
    if (window.confirm("Are you sure you want to delete this state?")) {
      deleteState(id);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-teal-700 rounded-xl flex items-center justify-center shadow-sm">
            <BadgePercent className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-teal-800">States</h1>
            <p className="mt-1 text-gray-600">Manage your states</p>
          </div>
        </div>

        <button
          onClick={() => openModal()}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 font-medium text-sm sm:text-base"
        >
          <Plus className="w-5 h-5" />
          Add State
        </button>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total" value={states.length} />
        <StatCard label="Active" value={states.filter((s) => s.status === "active").length} color="text-green-600" />
        <StatCard label="Inactive" value={states.filter((s) => s.status === "inactive").length} color="text-red-600" />
      </div>

      {/* FILTERS */}
      <div className="flex flex-col md:flex-row gap-4 bg-orange-50 border border-orange-200 rounded-xl p-4">
        <input
          placeholder="Search state..."
          className="px-4 py-2 border rounded-xl w-full md:w-80"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="px-4 py-2 border rounded-xl w-full md:w-52"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="">Sort By</option>
          <option value="az">Name A → Z</option>
          <option value="za">Name Z → A</option>
        </select>
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-6 py-3 font-semibold">ID</th>
              <th className="px-6 py-3 font-semibold">State</th>
              <th className="px-6 py-3 font-semibold">Status</th>
              <th className="px-6 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">
                <td className="px-6 py-4">{item.id}</td>
                <td className="px-6 py-4 font-medium">{item.name}</td>
                <td className="px-6 py-4">
                  <Toggle
                    active={item.status === "active"}
                    onToggle={() => toggleStatusHandler(item.id, item.status)}
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-end">
                    <RowActions
                      onEdit={() => openModal(item)}
                      onDelete={() => removeHandler(item.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARDS */}
      <div className="md:hidden space-y-4">
        {filtered.map((item) => (
          <div key={item.id} className="bg-white rounded-xl shadow p-4 space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">ID: {item.id}</p>
              <RowActions
                onEdit={() => openModal(item)}
                onDelete={() => removeHandler(item.id)}
              />
            </div>
            <p className="font-medium">{item.name}</p>
            <div className="flex items-center gap-3">
              <Toggle
                active={item.status === "active"}
                onToggle={() => toggleStatusHandler(item.id, item.status)}
              />
              <StatusBadge status={item.status} />
            </div>
          </div>
        ))}
      </div>

      {/* MODAL */}
      {modalOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/60" onClick={closeModal} />

            <div className="relative w-full max-w-md h-full bg-[#f3e7db] rounded-l-3xl shadow-2xl overflow-y-auto">

              {/* Modal Header */}
              <div className="relative p-6 border-b border-orange-200 text-center">
                <button
                  onClick={closeModal}
                  className="absolute top-4 left-4 w-9 h-9 bg-black text-white rounded-full flex items-center justify-center"
                >
                  <X size={16} />
                </button>
                <h2 className="text-2xl font-bold text-teal-700">
                  {editing ? "Edit State" : "Add New State"}
                </h2>
                <div className="w-12 h-[3px] bg-orange-400 mx-auto mt-2 rounded-full" />
              </div>

              {/* Modal Form */}
              <div className="p-6">
                <div className="bg-[#e6d5c3] rounded-2xl p-5 space-y-5">

                  {/* Name */}
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2 mb-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full" />
                      State Name *
                    </label>
                    <input
                      autoFocus
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter state name..."
                      maxLength={50}
                      className="w-full px-4 py-3 rounded-xl border border-orange-200 outline-none"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Keep it short and clear</span>
                      <span>{name.length}/50</span>
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2 mb-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full" />
                      Status
                    </label>
                    <div className="flex gap-3">
                      {["active", "inactive"].map((s) => (
                        <button
                          key={s}
                          onClick={() => setStatus(s)}
                          className={`px-4 py-2 rounded-full text-sm capitalize ${
                            status === s
                              ? s === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                </div>

                {/* Buttons */}
                <div className="flex gap-4 mt-6">
                  <button
                    onClick={closeModal}
                    className="flex-1 py-3 rounded-xl border bg-white text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={save}
                    disabled={!name.trim()}
                    className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-semibold disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* ---------- SUB-COMPONENTS ---------- */

const StatCard = ({ label, value, color = "text-gray-900" }) => (
  <div className="bg-white border border-orange-100 rounded-xl p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const StatusBadge = ({ status }) => (
  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
    status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
  }`}>
    {status}
  </span>
);

const Toggle = ({ active, onToggle }) => (
  <button
    onClick={onToggle}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      active ? "bg-green-600" : "bg-gray-300"
    }`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
      active ? "translate-x-6" : "translate-x-1"
    }`} />
  </button>
);

const RowActions = ({ onEdit, onDelete }) => (
  <div className="flex items-center gap-3">
    <button onClick={onEdit} className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50">
      <Edit3Icon size={16} />
    </button>
    <button onClick={onDelete} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50">
      <Trash2 size={16} />
    </button>
  </div>
);